<template>
  <div v-if="selectedActions?.length" class="w-full h-full">
    <div class="flex justify-between text-lg text-common-grey whitespace-nowrap">
      {{ $t("Filter") }}
      <div
        v-if="Object.keys(local_filter).length"
        class="!text-sm justify-center flex items-center float-left dark:bg-dark-box-background divide-light-divider-border dark:divide-dark-divider-border">
        {{ $t("This filter results in") }} {{ actions_count_text }}
      </div>
    </div>
    <div class="mb-1 text-sm text-common-grey whitespace-nowrap">
      {{ $t("Use filter if you want to narrow down results") }}
    </div>
    <div class="grid grid-cols-2 gap-2">
      <MultiSelectInput
        v-if="statuses_list.length"
        v-model:current-items="local_filter.status"
        :value="local_filter.status"
        name="status"
        :items="statuses_list"
        label-attr="name"
        value-attr="value"
        :allow-null="true"
        :label="$t('Status')"
        classes="w-full min-w-[200px]"
        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm  focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
        :placeholder="$t('Status')"
        :show-label="false" />
      <MultiSelectInput
        v-if="types_list.length"
        v-model:current-items="local_filter.types"
        :value="local_filter.types"
        name="type"
        :icon="user_menu_icon"
        :items="types_list"
        value-attr="value"
        label-attr="label"
        :allow-null="true"
        :label="$t('Types')"
        classes="w-full min-w-[200px]"
        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm  focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
        selection-text="Types (%COUNT)"
        :placeholder="$t('Type')"
        :show-label="false" />
      <MultiSelectInput
        v-if="selectedActions && selectedActions.includes('interactions')"
        v-model:current-items="local_filter.category"
        :value="local_filter.category"
        name="category"
        :icon="user_menu_icon"
        :items="$store.state.data.interaction_categories"
        value-attr="value"
        label-attr="label"
        :allow-null="true"
        :label="$t('Categories (only applies to interactions)')"
        classes="w-full min-w-[200px]"
        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm  focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
        selection-text="Categories (%COUNT)"
        :placeholder="$t('Category (only applies to interactions)')"
        :show-label="false" />
      <AsyncMultiSelectCompaniesInput
        :add-label="$t('Companies')"
        :disabled="disable_companies_selection"
        :this-record-selection="$route.name == 'CompanyProfile' || $route.name == 'ObjectProfile'"
        :clear-on-select="true"
        :is-box-background="false"
        classes="w-full min-w-[200px]"
        input-classes="appearance-none text-left block  px-3 py-2 border border-gray-300 rounded-md  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
        name="companies"
        value-attr="id"
        :initial-value="local_filter['companies']"
        :value="local_filter['companies']"
        :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
        endpoint-method="post"
        search-field="name"
        type="text"
        :placeholder="$t('Companies')"
        @update:currentItems="
          ($event) => {
            local_filter['companies'] = $event
          }
        " />

      <AsyncMultiSelectCompaniesInput
        object-type="contact"
        :add-label="$t('Contacts')"
        :clear-on-select="true"
        :is-box-background="false"
        classes="w-full min-w-[200px]"
        input-classes="w-full appearance-none text-left block  px-3 border rounded-md  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
        name="contacts"
        value-attr="id"
        :value="local_filter['contacts']"
        :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
        :disabled="disable_contacts_selection"
        :initial-value="local_filter['contacts']"
        endpoint-method="post"
        search-field="name"
        type="text"
        :placeholder="$t('Contacts')"
        @update:currentItems="
          ($event) => {
            local_filter['contacts'] = $event
          }
        " />

      <MultiSelectInput
        v-model:current-items="local_filter.collaborators"
        :value="local_filter.collaborators"
        name="assigned_to"
        :icon="user_menu_icon"
        :items="$store.state.data?.users"
        label-attr="full_name"
        :allow-null="true"
        value-attr="id"
        :label="$t('Users')"
        classes="w-full min-w-[200px] dark:text-dark-body-text"
        input-classes="w-full sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm  focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
        selection-text="Members (%COUNT)"
        :placeholder="$t('Team Members')"
        :show-label="false" />

      <RelativeDatePicker v-if="!hideRangeSelection" v-model:selected-relative-date="local_filter.relative_due_date" />
    </div>
  </div>
</template>

<script>
import { User } from "lucide-vue-next"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import AsyncMultiSelectCompaniesInput from "@/components/shared/AsyncMultiSelectCompaniesInput.vue"
import RelativeDatePicker from "@/components/shared/RelativeDatePicker.vue"
import dataService from "@/services/data.service"
import { utils } from "@/store/helpers"

export default {
  components: {
    RelativeDatePicker,
    MultiSelectInput,
    AsyncMultiSelectCompaniesInput
  },
  props: {
    initialFilter: null,
    selectedActions: null,
    dynamicDataSource: null,
    hideRangeSelection: {
      type: Boolean,
      default: false
    }
  },
  emits: ["change"],
  data() {
    return {
      disable_companies_selection: false,
      disable_contacts_selection: false,
      user_menu_icon: User,

      local_filter: {
        status: null,
        types: null,
        category: null,
        companies: null,
        contacts: null,
        collaborators: null,
        due_date_range: null,
        due_date_start: null,
        relative_due_date: "",
        due_date_end: null
      },
      filter: {
        status: null,
        types: null,
        category: null,
        companies: null,
        contacts: null,
        collaborators: null,
        due_date_range: null,
        due_date_start: null,
        due_date_end: null
      },
      results_count: { plans: 0, goals: 0, tasks: 0, interactions: 0 }
    }
  },
  computed: {
    actions_count_text() {
      let actions_count_text_calc = ""
      Object.keys(this.results_count).forEach((key, i) => {
        if (i == Object.keys(this.results_count).length - 1 && i) {
          actions_count_text_calc += ` and ${this.results_count[key]} ${key}`
        } else if (i) {
          actions_count_text_calc += `, ${this.results_count[key]} ${key}`
        } else {
          actions_count_text_calc += `${this.results_count[key]} ${key}`
        }
      })
      if (actions_count_text_calc) {
        return actions_count_text_calc
      }
      return this.$t("no results")
    },
    statuses_list() {
      const selection_list_calc = []
      if (this.selectedActions.includes("goals")) {
        this.$store.state.data.goal_statuses.forEach((s) => {
          if (!selection_list_calc.map((item) => item.value).includes(s.value)) {
            selection_list_calc.push(s)
          }
        })
      }
      if (this.selectedActions.includes("tasks")) {
        this.$store.state.data.task_statuses.forEach((s) => {
          if (!selection_list_calc.map((item) => item.value).includes(s.value)) {
            selection_list_calc.push(s)
          }
        })
      }
      if (this.selectedActions.includes("plans")) {
        this.$store.state.data.plan_statuses.forEach((s) => {
          if (!selection_list_calc.map((item) => item.value).includes(s.value)) {
            selection_list_calc.push(s)
          }
        })
      }
      return selection_list_calc
    },
    types_list() {
      const types_list_calc = []
      if (this.selectedActions.includes("plans")) {
        this.$store.state.data.plan_types.forEach((s) => {
          if (!types_list_calc.map((item) => item.value).includes(s.value)) {
            types_list_calc.push(s)
          }
        })
      }
      if (this.selectedActions.includes("goals")) {
        this.$store.state.data.goal_types.forEach((s) => {
          if (!types_list_calc.map((item) => item.value).includes(s.value)) {
            types_list_calc.push(s)
          }
        })
      }
      if (this.selectedActions.includes("interactions")) {
        this.$store.state.data.interaction_types.forEach((s) => {
          if (!types_list_calc.map((item) => item.value).includes(s.value)) {
            types_list_calc.push(s)
          }
        })
      }

      return types_list_calc
    }
  },
  watch: {
    local_filter: {
      deep: true,
      handler() {
        this.filter = { ...this.local_filter }
        // create a copy of local_filter with no empty values
        let clean_filter_copy = { ...this.local_filter }
        Object.keys(clean_filter_copy).forEach((key) => {
          if (utils.isEmpty(clean_filter_copy[key])) {
            delete clean_filter_copy[key]
          }
        })
        const clean_filter_copy_flat = { ...clean_filter_copy }

        if (clean_filter_copy_flat.companies?.length) {
          clean_filter_copy_flat.companies = clean_filter_copy_flat.companies.map((c) => c.id)
        }
        if (clean_filter_copy_flat.contacts?.length) {
          clean_filter_copy_flat.contacts = clean_filter_copy_flat.contacts.map((c) => c.id)
        }

        this.$emit("change", clean_filter_copy_flat)
        this.getSelectedActionsTotoalCount()
      }
    },
    selectedActions: {
      deep: true,
      handler() {
        this.local_filter = {
          status: null,
          types: null,
          category: null
        }
      }
    }
  },
  created() {
    if (this.initialFilter) {
      const promises = []
      const intial_filter_full = { ...this.initialFilter }
      let add_this_record = false
      if (this.initialFilter.companies?.length) {
        this.initialFilter.companies.forEach((company_id) => {
          if (company_id == "this_record") {
            add_this_record = true
          } else {
            promises.push(this.$store.dispatch("data/getRecord", company_id))
          }
        })
      }

      if (this.initialFilter.contacts?.length) {
        this.initialFilter.contacts.forEach((contact_id) => {
          promises.push(this.$store.dispatch("data/getRecord", contact_id))
        })
      }

      Promise.all(promises).then((values) => {
        const companies_calc = []
        const contacts_calc = []

        if (add_this_record) {
          companies_calc.push({ id: "this_record" })
        }

        values.forEach((res_value) => {
          if (res_value.data.type == "contact") {
            contacts_calc.push(res_value.data)
          } else {
            companies_calc.push(res_value.data)
          }
        })
        intial_filter_full.companies = companies_calc
        intial_filter_full.contacts = contacts_calc

        this.local_filter = intial_filter_full
      })

      if (add_this_record) {
        const companies_calc = [{ id: "this_record" }]
        intial_filter_full.companies = companies_calc
        this.local_filter = intial_filter_full
      }
    }
    if (this.dynamicDataSource?.object_type == "company") {
      this.$store.dispatch("data/getRecord", this.dynamicDataSource?.id).then((res) => {
        this.local_filter["companies"] = [res.data]
        this.disable_companies_selection = true
      })
    }
    if (this.dynamicDataSource?.object_type == "contact") {
      this.$store.dispatch("data/getRecord", this.dynamicDataSource?.id).then((res) => {
        this.local_filter["contacts"] = [res.data]
        this.disable_contacts_selection = true

        if (res.data.parent) {
          setTimeout(() => {
            this.$store.dispatch("data/getRecord", res.data.parent).then((res) => {
              this.local_filter["companies"] = [res.data]
              this.disable_companies_selection = true
            })
          }, 1100)
        }
      })
    }

    this.getSelectedActionsTotoalCount()
  },

  methods: {
    getSelectedActionsTotoalCount() {
      return this.$utils.debounce(() => {
        const filter_clone = this.$utils.clone(this.filter)
        this.results_count = {}
        if (this.local_filter["companies"]) {
          filter_clone["companies"] = this.local_filter["companies"]
            .map((c) => c.id)
            .join(",")
            .replaceAll("this_record", this.$route.params.cid)
          filter_clone["records"] = this.local_filter["companies"]
            .map((c) => c.id)
            .join(",")
            .replaceAll("this_record", this.$route.params.cid)
        }
        if (this.local_filter["contacts"]) {
          filter_clone["contacts"] = this.local_filter["contacts"].map((c) => c.id).join(",")
        }
        if (this.local_filter["collaborators"]) {
          filter_clone["collaborators"] = this.local_filter["collaborators"].map((c) => c.id).join(",")
          filter_clone["assigned_to"] = this.local_filter["collaborators"].map((c) => c.id).join(",")
        }
        if (this.local_filter["category"]) {
          filter_clone["category"] = this.local_filter["category"].join(",")
        }
        if (this.local_filter["status"]) {
          filter_clone["status"] = this.local_filter["status"].join(",")
        }

        if (this.selectedActions?.includes("plans")) {
          const other_params = {}
          if (this.local_filter["types"]) {
            other_params["type"] = this.$store.state.data.plan_types
              .filter((t) => this.local_filter["types"].includes(t.value))
              .map((t) => t.value)
              .join(",")
          }
          dataService.getPlans({ ...filter_clone, ...other_params }).then((res) => {
            if (res.data.count) {
              this.results_count["plans"] = res.data.count
            }
          })
        }
        if (this.selectedActions?.includes("goals")) {
          const other_params = {}
          if (this.local_filter["types"]) {
            other_params["type"] = this.$store.state.data.goal_types
              .filter((t) => this.local_filter["types"].includes(t.value))
              .map((t) => t.value)
              .join(",")
          }
          dataService.getGoals({ ...filter_clone, ...other_params }).then((res) => {
            if (res.data.count) {
              this.results_count["goals"] = res.data.count
            }
          })
        }
        if (this.selectedActions?.includes("tasks")) {
          dataService.getTasks({ ...filter_clone }).then((res) => {
            if (res.data.count) {
              this.results_count["tasks"] = res.data.count
            }
          })
        }
        if (this.selectedActions?.includes("interactions")) {
          const other_params = {}
          if (this.local_filter["types"]) {
            other_params["type"] = this.$store.state.data.interaction_types
              .filter((t) => this.local_filter["types"].includes(t.value))
              .map((t) => t.value)
              .join(",")
          }
          dataService.getAllInteractions({ ...filter_clone, ...other_params }).then((res) => {
            if (res.data.count) {
              this.results_count["interactions"] = res.data.count
            }
          })
        }
      }, 800)
    }
  }
}
</script>
